import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Download, RefreshCcw } from "lucide-react";
import { useFetch } from "../lib/api";
import { SectionLabel } from "../components/shared";

export function Tools() {
  const { data: tools } = useFetch("/tools");
  return (
    <main className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14" data-testid="tools-page">
      <SectionLabel color="#12B76A">Free interactive tools</SectionLabel>
      <h1 className="font-display font-bold text-[38px] lg:text-[54px] tracking-tight">Tools every US compliance team <span className="text-[#12B76A]">actually uses.</span></h1>
      <p className="mt-4 text-[16px] text-[#1A1914]/60 max-w-2xl">No login required. Instant results. Share with your team.</p>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tools-grid">
        {(tools || []).map((t) => (
          <Link key={t.slug} to={`/tools/${t.slug}`} data-testid={`tool-card-${t.slug}`} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-6 group flex flex-col">
            <div className="flex items-center justify-between">
              {t.badge ? <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full bg-[#12B76A]/10 border border-[#12B76A]/25 text-[#0E9355]">{t.badge}</span> : <span />}
              <span className="font-mono-label text-[10px] text-[#1A1914]/40">{t.time}</span>
            </div>
            <h3 className="mt-4 font-display font-semibold text-[21px] group-hover:text-[#12B76A] transition-colors">{t.name}</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-[#1A1914]/60">{t.desc}</p>
            <p className="mt-4 pt-4 border-t border-[#E7E0D2] inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#1A1914] group-hover:text-[#12B76A] mt-auto rp-btn">Open tool <ArrowRight size={14} /></p>
          </Link>
        ))}
      </div>
    </main>
  );
}

const maturityQs = [
  "We maintain a documented, board-approved risk appetite statement.",
  "Risk assessments feed directly into budget and strategy decisions.",
  "We have a named owner for every top-10 enterprise risk.",
  "Incident reporting clocks (SEC/CISA/state) are mapped and rehearsed.",
  "Controls are tested on a schedule — not just before audits.",
  "Third-party/vendor risk is tiered with continuous monitoring.",
  "KRIs with thresholds trigger documented escalation.",
  "The board receives a risk dashboard at least quarterly."
];

function GrcMaturity() {
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const pct = Math.round((score / (maturityQs.length * 2)) * 100);
  const tier = pct >= 75 ? { name: "Managed", color: "#0E9355", advice: "You're audit-ready. Focus on quantification (FAIR) and continuous controls monitoring." } : pct >= 40 ? { name: "Developing", color: "#B45309", advice: "Solid foundation. Prioritize KRI thresholds, vendor tiering and rehearsing your reporting clocks." } : { name: "Initial", color: "#DC2626", advice: "Start with NIST CSF 2.0 as your skeleton, name risk owners, and map your regulatory deadlines this quarter." };
  return (
    <div className="max-w-2xl">
      {!done ? (
        <div className="space-y-4">
          {maturityQs.map((q, i) => (
            <div key={i} className="bg-white border border-[#E7E0D2] rounded-xl p-5">
              <p className="text-[14.5px] font-medium">{i + 1}. {q}</p>
              <div className="mt-3 flex gap-2">
                {[["No", 0], ["Partially", 1], ["Yes", 2]].map(([label, val]) => (
                  <button key={label} onClick={() => setAnswers({ ...answers, [i]: val })} data-testid={`maturity-q${i}-${label.toLowerCase()}`}
                    className={`px-4 py-2 text-[13px] font-medium rounded-lg border rp-btn ${answers[i] === val ? "bg-[#1A1914] text-white border-[#1A1914]" : "bg-[#FAF7F1] border-[#E7E0D2] hover:border-[#1A1914]/40"}`}>{label}</button>
                ))}
              </div>
            </div>
          ))}
          <button disabled={Object.keys(answers).length < maturityQs.length} onClick={() => setDone(true)} data-testid="maturity-submit"
            className="w-full px-6 py-3.5 text-[15px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-xl rp-btn disabled:opacity-40 disabled:cursor-not-allowed">
            See my scorecard ({Object.keys(answers).length}/{maturityQs.length} answered)
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#E7E0D2] rounded-2xl p-8 rp-fade-up" data-testid="maturity-result">
          <p className="font-mono-label text-[10px] text-[#1A1914]/40">Your GRC maturity</p>
          <p className="mt-2 font-display font-bold text-[52px]" style={{ color: tier.color }}>{pct}%</p>
          <p className="font-display font-semibold text-[24px]">{tier.name}</p>
          <div className="mt-4 h-3 rounded-full bg-[#F3EEE4] overflow-hidden"><div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, background: tier.color }} /></div>
          <p className="mt-5 text-[14.5px] leading-relaxed text-[#1A1914]/70">{tier.advice}</p>
          <button onClick={() => { setAnswers({}); setDone(false); }} className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-[#3F4CE0] hover:underline"><RefreshCcw size={14} /> Retake</button>
        </div>
      )}
    </div>
  );
}

function ComplianceCost() {
  const [entity, setEntity] = useState("public");
  const [size, setSize] = useState(2);
  const sizes = ["< 100 employees", "100–1,000", "1,000–10,000", "10,000+"];
  const base = { public: [1.2, 4.8, 18.5, 62], bank: [2.1, 8.4, 31, 120], insurer: [1.6, 6.2, 24, 88], localgov: [0.4, 1.5, 5.8, 19], msb: [1.8, 7.1, 27, 96] };
  const labels = { public: "Public company (SEC)", bank: "Bank / credit union", insurer: "Insurer (NYDFS/NAIC)", localgov: "Local government", msb: "Fintech / MSB (FinCEN)" };
  const exposure = base[entity][size];
  return (
    <div className="max-w-2xl">
      <div className="bg-white border border-[#E7E0D2] rounded-2xl p-7">
        <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-3">Entity type</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(labels).map(([k, v]) => (
            <button key={k} onClick={() => setEntity(k)} data-testid={`cost-entity-${k}`} className={`px-3.5 py-2 text-[13px] font-medium rounded-lg border rp-btn ${entity === k ? "bg-[#1A1914] text-white border-[#1A1914]" : "bg-[#FAF7F1] border-[#E7E0D2] hover:border-[#1A1914]/40"}`}>{v}</button>
          ))}
        </div>
        <p className="font-mono-label text-[10px] text-[#1A1914]/40 mt-6 mb-3">Organization size — {sizes[size]}</p>
        <input type="range" min="0" max="3" value={size} onChange={(e) => setSize(Number(e.target.value))} data-testid="cost-size-slider" className="w-full accent-[#3F4CE0]" />
        <div className="mt-7 p-6 rounded-2xl bg-[#E5484D]/5 border border-[#E5484D]/20" data-testid="cost-result">
          <p className="font-mono-label text-[10px] text-[#DC2626]">Estimated worst-case annual penalty exposure</p>
          <p className="mt-1 font-display font-bold text-[46px] text-[#DC2626]">${exposure}M</p>
          <p className="text-[13px] text-[#1A1914]/60 leading-relaxed">Aggregated across applicable regimes (enforcement history-based heuristic). Educational estimate only — not legal advice.</p>
        </div>
      </div>
    </div>
  );
}

function DeadlineCalendar() {
  const { data: deadlines } = useFetch("/deadlines");
  return (
    <div className="max-w-3xl">
      <div className="overflow-hidden rounded-2xl border border-[#E7E0D2] bg-white">
        <table className="w-full text-left" data-testid="deadline-table">
          <thead><tr className="border-b border-[#E7E0D2] bg-[#F3EEE4]">{["Date", "Body", "Deadline"].map((h) => <th key={h} className="px-5 py-3.5 font-mono-label text-[10px] text-[#1A1914]/50">{h}</th>)}</tr></thead>
          <tbody>
            {(deadlines || []).map((d) => (
              <tr key={d.item} className="border-b border-[#E7E0D2]/60 last:border-0 hover:bg-[#FAF7F1] transition-colors">
                <td className="px-5 py-4 font-mono-label text-[11.5px] whitespace-nowrap">{d.date}</td>
                <td className="px-5 py-4"><span className="font-mono-label text-[10px] px-2 py-0.5 rounded-full bg-[#3F4CE0]/5 border border-[#3F4CE0]/20 text-[#3F4CE0]">{d.body}</span></td>
                <td className="px-5 py-4 text-[13.5px]">{d.item}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => alert("The .ics download will be enabled with the backend.")} className="mt-5 inline-flex items-center gap-2 px-5 py-3 text-[14px] font-semibold text-white bg-[#12B76A] hover:bg-[#0E9355] rounded-xl rp-btn" data-testid="ics-download"><Download size={15} /> Download .ics for Outlook / Google</button>
    </div>
  );
}

export function ToolDetail() {
  const { slug } = useParams();
  const { data: tools } = useFetch("/tools");
  const t = (tools || []).find((x) => x.slug === slug);
  const body = slug === "grc-maturity" ? <GrcMaturity /> : slug === "compliance-cost" ? <ComplianceCost /> : slug === "deadline-calendar" ? <DeadlineCalendar /> : (
    <div className="bg-white border border-[#E7E0D2] rounded-2xl p-8 max-w-xl"><p className="text-[15px] text-[#1A1914]/65">This tool ships with the full backend release. <Link to="/signup" className="text-[#3F4CE0] font-semibold hover:underline">Get notified →</Link></p></div>
  );
  return (
    <main className="max-w-[1100px] mx-auto px-5 lg:px-8 py-12" data-testid="tool-detail-page">
      <Link to="/tools" className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#1A1914]/60 hover:text-[#3F4CE0] rp-btn"><ArrowLeft size={14} /> All tools</Link>
      <h1 className="mt-6 font-display font-bold text-[32px] lg:text-[44px] tracking-tight">{t ? t.name : "Tool"}</h1>
      {t && <p className="mt-3 text-[15px] text-[#1A1914]/60 max-w-2xl">{t.desc}</p>}
      <div className="mt-8">{body}</div>
    </main>
  );
}
